import { useRouter } from 'next/router'
import Dropdown from 'rc-dropdown'
import { useState } from 'react'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'

import { Divider } from 'src/components/atoms/Divider'
import { InternalLink } from 'src/components/atoms/Link'
import { LinkOption, LinkOptionType } from 'src/components/layouts/Header/types'

import { ArrayElement } from 'src/types/common'

export type MenuItemProps = {
  option: ArrayElement<NonNullable<LinkOption['options']>>
  onClick?: () => void
}

export const MenuItem = ({ option, onClick }: MenuItemProps) => {
  const router = useRouter()

  if (option.type === LinkOptionType.Seperator)
    return <Divider className='m-0! border-0 h-px bg-white/10' />

  const isExtendedMenuItem = !!option.description
  const isActive = router?.pathname === option.path

  return (
    <InternalLink
      href={option.path}
      openInNewTab={option.openInNewWindow}
      color='white'
      onClick={onClick}
      className='hover:text-white font-medium'
    >
      <div
        className={`flex flex-row pl-10 lg:px-6 px-5 ${
          isExtendedMenuItem ? 'lg:py-5 py-3.75' : 'py-4'
        } gap-2.5 hover:bg-frostWhite/20 ${
          isActive ? 'bg-frostWhite/20 font-semibold' : ''
        }`}
      >
        <div className='flex flex-col gap-1.25'>
          <span
            className={`text-[16px] leading-6 uppercase ${
              isExtendedMenuItem ? 'font-semibold' : 'font-medium'
            }`}
          >
            {option.label}
          </span>
          {option.description ? (
            <span className='text-steelGrey text-[14px] leading-5 font-medium'>
              {option.description}
            </span>
          ) : null}
        </div>
      </div>
    </InternalLink>
  )
}

export type SubMenuProps = {
  subMenu: NonNullable<LinkOption['subMenu']>
}

export const SubMenu = ({ subMenu }: SubMenuProps) => {
  return (
    <div className='flex flex-col lg:pl-0 pl-5 bg-aquaSpring/60 pt-5 pb-0 min-w-50'>
      <span className='text-[14px] font-medium uppercase text-silverDust mx-5'>
        {subMenu.title}
      </span>
      {subMenu.subMenuOptions?.map((subMenuItem, i) => {
        if (subMenuItem.type === LinkOptionType.Seperator)
          return <div key={i} />

        return (
          <InternalLink
            key={`${subMenuItem.label}_${i}`}
            color='black'
            href={subMenuItem.path as string}
            highlightActive
          >
            <div className='text-[16px] font-medium py-3.5 hover:bg-frostWhite/20 px-5'>
              <span
                className='truncate max-w-50 uppercase'
                title={subMenuItem.label}
              >
                {subMenuItem.label}
              </span>
            </div>
          </InternalLink>
        )
      })}
    </div>
  )
}

export type MenuDropdownProps = {
  menuItem: LinkOption
  linkColorClass?: 'text-white' | 'text-shark'
}

export const MenuDropdown = ({
  menuItem: { label, options = [], subMenu },
  linkColorClass = 'text-shark'
}: MenuDropdownProps) => {
  const [visible, setVisible] = useState<boolean>(false)

  return (
    <Dropdown
      animation='slide-up'
      onVisibleChange={v => {
        setVisible(v)
      }}
      placement='bottomLeft'
      visible={visible}
      overlay={
        <div className='font-body-rebrand flex flex-row bg-forestDeep/60 backdrop-blur-sm rounded-2xl overflow-hidden'>
          <div className='max-w-100 min-w-60'>
            {options.map((option, i, arr) => {
              return (
                <>
                  <MenuItem
                    onClick={() => {
                      setVisible(false)
                    }}
                    key={i}
                    option={option}
                  />
                  {i < arr.length - 1 ? (
                    <MenuItem
                      option={{
                        type: LinkOptionType.Seperator
                      }}
                    />
                  ) : null}
                </>
              )
            })}
          </div>
          {subMenu && subMenu.subMenuOptions?.length ? (
            <SubMenu subMenu={subMenu} />
          ) : null}
        </div>
      }
    >
      <div
        className={`link ${linkColorClass} ${
          visible ? 'dropdown-visible' : ''
        } h-11 flex cursor-default gap-1.25 text-nowrap items-center text-[16px] leading-6 font-medium uppercase px-6 rounded-full transition-colors hover:bg-forestDeep/60 hover:text-white`}
      >
        {label}
        <span>
          {visible ? (
            <MdKeyboardArrowUp fontWeight='bold' size='20px' />
          ) : (
            <MdKeyboardArrowDown fontWeight='bold' size='20px' />
          )}
        </span>
      </div>
    </Dropdown>
  )
}
